/**
 * RoamerProfileService.ts — persistence + change broadcasting for RoamerProfile.
 *
 * Mirrors the Roamer Location Engine™ contract:
 *   • Single mutation path: updateProfile() — the ONLY way the profile changes.
 *   • Persists one versioned blob to localStorage ('nomad:roamer-profile:v1').
 *   • Emits in-process subscribers + window CustomEvent('nomad:roamer-profile-changed')
 *     so future onboarding / settings UIs and other engines stay in sync.
 *   • All read/write boundaries hand out CLONES — internal state never leaks
 *     by reference (lesson from Engine #1's code review).
 */

import {
  DEFAULT_ROAMER_PROFILE,
  mergeProfile,
  profileSignature,
  type RoamerProfile,
} from './RoamerProfile';
import { storageManager } from '../storage/StorageManager';

const STORAGE_KEY = 'nomad:roamer-profile:v1';
export const PROFILE_CHANGED_EVENT = 'nomad:roamer-profile-changed';

export type ProfileListener = (profile: RoamerProfile) => void;

function cloneProfile(p: RoamerProfile): RoamerProfile {
  return { ...p };
}

function hasStorage(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch {
    return false;
  }
}

export class RoamerProfileService {
  private profile: RoamerProfile;
  private listeners = new Set<ProfileListener>();

  constructor() {
    this.profile = this.load();
    // Cloud hydration (write-through cache model): localStorage answers all
    // synchronous reads; if the cloud copy is strictly newer we adopt it and
    // broadcast through the normal change channel. Never blocks construction.
    void this.hydrateFromCloud();
  }

  private async hydrateFromCloud(): Promise<void> {
    const cloud = await storageManager.hydrate<Partial<RoamerProfile>>('profile', 'roamer-profile');
    if (!cloud) return;
    const next = mergeProfile({ ...DEFAULT_ROAMER_PROFILE }, cloud);
    if (profileSignature(next) === profileSignature(this.profile)) return;
    this.profile = next;
    this.persistLocal();
    this.emit();
  }

  // ── Reads (always cloned) ────────────────────────────────────────────────────

  getProfile(): RoamerProfile {
    return cloneProfile(this.profile);
  }

  getSignature(): string {
    return profileSignature(this.profile);
  }

  // ── Single mutation path ─────────────────────────────────────────────────────

  updateProfile(patch: Partial<RoamerProfile>): RoamerProfile {
    const next = mergeProfile(this.profile, patch);
    if (profileSignature(next) === profileSignature(this.profile)) {
      return cloneProfile(this.profile); // no material change — no persist/emit
    }
    this.profile = next;
    this.persist();
    this.emit();
    return cloneProfile(this.profile);
  }

  resetProfile(): RoamerProfile {
    return this.updateProfile({ ...DEFAULT_ROAMER_PROFILE });
  }

  // ── Subscriptions ────────────────────────────────────────────────────────────

  onProfileChanged(fn: ProfileListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  // ── Internals ────────────────────────────────────────────────────────────────

  private emit(): void {
    const snapshot = cloneProfile(this.profile);
    this.listeners.forEach((fn) => {
      try { fn(cloneProfile(snapshot)); } catch { /* listener errors are isolated */ }
    });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(PROFILE_CHANGED_EVENT, { detail: { profile: snapshot } }),
      );
    }
  }

  private persist(): void {
    this.persistLocal();
    // Write-through to cloud via the gateway (queues offline, never throws).
    storageManager.push('profile', 'roamer-profile', this.profile);
  }

  private persistLocal(): void {
    if (!hasStorage()) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.profile));
    } catch { /* storage full/blocked — profile stays in memory */ }
  }

  private load(): RoamerProfile {
    if (!hasStorage()) return { ...DEFAULT_ROAMER_PROFILE };
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return { ...DEFAULT_ROAMER_PROFILE };
      const parsed = JSON.parse(raw) as Partial<RoamerProfile>;
      // Merge over defaults so newly-added fields are always present.
      return mergeProfile({ ...DEFAULT_ROAMER_PROFILE }, parsed);
    } catch {
      return { ...DEFAULT_ROAMER_PROFILE };
    }
  }
}

/** App-wide singleton — import this, do not instantiate your own. */
export const roamerProfileService = new RoamerProfileService();
